/**
 * ParetoChart — generic bars-sorted-by-count + cumulative-% line overlay.
 *
 * Industry-agnostic. The foundry pack's DefectParetoChart specialises this
 * with defect-style colouring; this widget is the general-purpose primitive
 * usable for any frequency distribution.
 */

import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/ui/cn";
import { ChartSkeleton } from "../ChartSkeleton";
import type { WidgetState } from "../types";
import { SERIES_PALETTE } from "../series-palette";

export interface ParetoBucket {
  name: string;
  count: number;
}

export interface ParetoChartProps {
  data: ParetoBucket[];
  /** Bars color: default the first chart series colour (series-palette). */
  barColor?: string;
  /** Cumulative line color: default the second chart series colour. */
  lineColor?: string;
  height?: number;
  state?: WidgetState;
  className?: string;
}

export function ParetoChart({
  data,
  barColor = SERIES_PALETTE[0],
  lineColor = SERIES_PALETTE[1],
  height = 280,
  state = { kind: "ok" },
  className,
}: ParetoChartProps) {
  const computed = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.count - a.count);
    const total = sorted.reduce((s, d) => s + d.count, 0) || 1;
    let cum = 0;
    return sorted.map((d) => {
      cum += d.count;
      return {
        name: d.name,
        count: d.count,
        cumPct: Math.round((cum / total) * 100),
      };
    });
  }, [data]);

  if (state.kind === "loading") {
    return <ChartSkeleton shape="pareto" height={height} className={className} />;
  }
  if (state.kind === "empty" || data.length === 0) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center rounded-md border border-dashed border-border bg-surface-elevated/40 text-sm text-on-surface-muted",
          className,
        )}
        style={{ height }}
      >
        No data.
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={computed} margin={{ top: 8, right: 24, bottom: 28, left: 8 }}>
          <CartesianGrid stroke="rgb(var(--color-border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="name"
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 10 }}
            angle={-20}
            textAnchor="end"
            interval={0}
            height={48}
          />
          <YAxis yAxisId="left" stroke="rgb(var(--color-on-surface-muted))" tick={{ fontSize: 11 }} />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, 100]}
            stroke="rgb(var(--color-on-surface-muted))"
            tick={{ fontSize: 11 }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "rgb(var(--color-surface-elevated))",
              border: "1px solid rgb(var(--color-border))",
              borderRadius: 6,
              fontSize: 12,
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="left" dataKey="count" fill={barColor} name="Count" radius={[3, 3, 0, 0]} />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="cumPct"
            stroke={lineColor}
            strokeWidth={2}
            dot={{ r: 3 }}
            name="Cumulative %"
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
